/**
 * PPT (Paper Folding Test) Task Generator
 *
 * Generates procedural cube-folding tasks.
 * The 3D cube renderer shows THREE visible faces:
 *   - front  (large, nearly square, left side)
 *   - right  (narrow parallelogram, right side — recedes at ~45°)
 *   - top    (narrow parallelogram, top — recedes at ~45°)
 *
 * Cube face indices (array order):
 *   0=front  1=right  2=top  3=back  4=left  5=bottom
 *
 * Dot patterns are varied and asymmetric to match the DLR reference images.
 * Coordinates are [x,y] in [0,1]×[0,1] where (0,0)=top-left of the flat face.
 */

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const shuffle = (arr) => { const a = [...arr]; for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; };

// ─── Dot Patterns ────────────────────────────────────────────────────────────
// Asymmetric patterns so rotations look different. Modeled after the reference.
const DOT_PATTERNS = [
  { id: 1,  dots: [[0.5, 0.5]] },                                                           // 1 center
  { id: 2,  dots: [[0.3, 0.3], [0.7, 0.7]] },                                               // 2 diagonal ↘
  { id: 3,  dots: [[0.7, 0.3], [0.3, 0.7]] },                                               // 2 diagonal ↙
  { id: 4,  dots: [[0.3, 0.3], [0.5, 0.5], [0.7, 0.7]] },                                   // 3 diagonal ↘
  { id: 5,  dots: [[0.3, 0.3], [0.7, 0.3], [0.5, 0.7]] },                                   // 3 triangle ▽
  { id: 6,  dots: [[0.3, 0.3], [0.3, 0.7], [0.7, 0.3], [0.7, 0.7]] },                       // 4 corners
  { id: 7,  dots: [[0.3, 0.3], [0.7, 0.3], [0.7, 0.5], [0.7, 0.7]] },                       // 4 L-right
  { id: 8,  dots: [[0.3, 0.3], [0.3, 0.5], [0.3, 0.7], [0.7, 0.7]] },                       // 4 L-left
  { id: 9,  dots: [[0.5, 0.3], [0.3, 0.7], [0.7, 0.7]] },                                   // 3 triangle ▲ inverted
  { id: 10, dots: [[0.3, 0.3], [0.3, 0.7], [0.5, 0.5], [0.7, 0.3], [0.7, 0.7]] },           // 5 X-pattern
  { id: 11, dots: [[0.3, 0.3], [0.3, 0.5], [0.3, 0.7], [0.7, 0.3], [0.7, 0.5], [0.7, 0.7]] }, // 6 two-col
  { id: 12, dots: [[0.3, 0.3], [0.5, 0.3], [0.7, 0.3], [0.3, 0.7], [0.5, 0.7], [0.7, 0.7]] }, // 6 two-row
  { id: 13, dots: [[0.5, 0.25], [0.5, 0.5], [0.5, 0.75]] },                                 // 3 vertical
  { id: 14, dots: [[0.3, 0.3], [0.7, 0.3], [0.3, 0.7]] },                                   // 3 L top-left
  { id: 15, dots: [[0.3, 0.3], [0.5, 0.3], [0.7, 0.5], [0.7, 0.7]] },                       // 4 staircase ↘
  { id: 16, dots: [[0.3, 0.5], [0.5, 0.3], [0.7, 0.5], [0.5, 0.7]] },                       // 4 diamond
  { id: 17, dots: [[0.25, 0.3], [0.5, 0.3], [0.75, 0.3], [0.5, 0.5], [0.5, 0.75]] },        // 5 T-shape
  { id: 18, dots: [[0.3, 0.3], [0.5, 0.5], [0.7, 0.3], [0.7, 0.7]] },                       // 4 Z-shape
  { id: 19, dots: [[0.3, 0.3], [0.7, 0.3], [0.3, 0.5], [0.3, 0.7], [0.7, 0.7], [0.5, 0.5]] }, // 6 scattered
  { id: 20, dots: [[0.5, 0.3], [0.3, 0.5], [0.7, 0.5]] },                                   // 3 arrow ↑
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const rotateDots = (dots, angle) => {
  if (angle === 0) return dots.map(d => [...d]);
  const rad = (angle * Math.PI) / 180;
  const c = Math.round(Math.cos(rad) * 1e4) / 1e4;
  const s = Math.round(Math.sin(rad) * 1e4) / 1e4;
  return dots.map(([x, y]) => {
    const nx = x - 0.5, ny = y - 0.5;
    return [
      Math.round((nx * c - ny * s + 0.5) * 1e4) / 1e4,
      Math.round((nx * s + ny * c + 0.5) * 1e4) / 1e4
    ];
  });
};

const mirrorDotsH = (dots) => dots.map(([x, y]) => [1 - x, y]);

// ─── Cube configuration ─────────────────────────────────────────────────────
// Returns 6 faces: [front, right, top, back, left, bottom]
// Each face: { patternId, baseDots, rotation, dots }
const generateCube = () => {
  const patterns = shuffle(DOT_PATTERNS).slice(0, 6);
  return patterns.map(p => {
    const rot = pick([0, 90, 180, 270]);
    return {
      patternId: p.id,
      baseDots: p.dots,
      rotation: rot,
      dots: rotateDots(p.dots, rot),
    };
  });
};

// ─── Build a 3D cube view ────────────────────────────────────────────────────
// The renderer expects { top: {dots}, front: {dots}, right: {dots} }
const buildView = (faces, topIdx, frontIdx, rightIdx) => ({
  top:   { ...faces[topIdx] },
  front: { ...faces[frontIdx] },
  right: { ...faces[rightIdx] },
});

const viewKey = (v) => {
  const fk = (f) => `${f.patternId}:${f.rotation}`;
  return `${fk(v.top)}|${fk(v.front)}|${fk(v.right)}`;
};

// ─── Generate incorrect cubes ────────────────────────────────────────────────
const makeIncorrect = (correct, allFaces) => {
  const cl = () => JSON.parse(JSON.stringify(correct));
  const fns = ['top', 'front', 'right'];
  const f = pick(fns);

  const mutations = [
    // Rotate one face 90°
    () => { const c = cl(); c[f].rotation = (c[f].rotation + 90) % 360; c[f].dots = rotateDots(c[f].baseDots, c[f].rotation); return c; },
    // Rotate one face 180°
    () => { const c = cl(); c[f].rotation = (c[f].rotation + 180) % 360; c[f].dots = rotateDots(c[f].baseDots, c[f].rotation); return c; },
    // Swap two visible faces
    () => { const c = cl(); const a = pick(fns); let b = pick(fns); while (b === a) b = pick(fns); const t = c[a]; c[a] = c[b]; c[b] = t; return c; },
    // Replace one face with an unused pattern
    () => {
      const c = cl();
      const usedIds = new Set(fns.map(n => c[n].patternId));
      const avail = DOT_PATTERNS.filter(p => !usedIds.has(p.id));
      if (!avail.length) return c;
      const np = pick(avail);
      c[f].patternId = np.id;
      c[f].baseDots = np.dots;
      c[f].dots = rotateDots(np.dots, c[f].rotation);
      return c;
    },
    // Mirror one face horizontally
    () => { const c = cl(); c[f].dots = mirrorDotsH(c[f].dots); return c; },
  ];

  return pick(mutations)();
};

// ─── Net layouts ─────────────────────────────────────────────────────────────
// Maps face names → {row, col} in the grid.
// Face order in array: [front, right, top, back, left, bottom]
// faceNames below matches that order.
const FACE_NAMES = ['front', 'right', 'top', 'back', 'left', 'bottom'];

const NET_LAYOUTS = [
  // Standard cross
  //       [top]
  // [left][front][right][back]
  //       [bottom]
  { top:{r:0,c:1}, left:{r:1,c:0}, front:{r:1,c:1}, right:{r:1,c:2}, back:{r:1,c:3}, bottom:{r:2,c:1} },

  // T-shape (top row + extensions)
  //       [top]
  // [left][front][right][back]
  //              [bottom]
  { top:{r:0,c:1}, left:{r:1,c:0}, front:{r:1,c:1}, right:{r:1,c:2}, back:{r:1,c:3}, bottom:{r:2,c:2} },

  // Staircase descending-right (like ref 002937)
  //             [top]
  //       [back][right]
  // [left][front]
  // [bottom]
  { top:{r:0,c:2}, back:{r:1,c:1}, right:{r:1,c:2}, left:{r:2,c:0}, front:{r:2,c:1}, bottom:{r:3,c:0} },

  // Staircase descending-left (like ref 002949)
  //                   [top]
  //             [front][right]
  //       [left][back]
  // [bottom]
  { top:{r:0,c:2}, front:{r:1,c:1}, right:{r:1,c:2}, left:{r:2,c:0}, back:{r:2,c:1}, bottom:{r:3,c:0} },

  // L-shape
  // [top][front][right][back]
  //      [bottom]
  //      [left]
  { top:{r:0,c:0}, front:{r:0,c:1}, right:{r:0,c:2}, back:{r:0,c:3}, bottom:{r:1,c:1}, left:{r:2,c:1} },

  // Cross variant 2
  //       [top]
  // [back][front][right]
  //       [bottom]
  //       [left]
  { top:{r:0,c:1}, back:{r:1,c:0}, front:{r:1,c:1}, right:{r:1,c:2}, bottom:{r:2,c:1}, left:{r:3,c:1} },
];

// ─── Generate one PPT task ───────────────────────────────────────────────────
export const generatePPTTask = (taskNumber) => {
  const faces = generateCube(); // [front=0, right=1, top=2, back=3, left=4, bottom=5]

  // Build unfolded net
  const layout = pick(NET_LAYOUTS);
  const unfoldedPattern = {};
  let maxR = 0, maxC = 0;
  FACE_NAMES.forEach((name, idx) => {
    const pos = layout[name];
    unfoldedPattern[name] = {
      ...faces[idx],
      gridRow: pos.r,
      gridCol: pos.c,
    };
    maxR = Math.max(maxR, pos.r);
    maxC = Math.max(maxC, pos.c);
  });
  unfoldedPattern._rows = maxR + 1;
  unfoldedPattern._cols = maxC + 1;

  // Correct 3D view: top=faces[2], front=faces[0], right=faces[1]
  const correctView = buildView(faces, 2, 0, 1);
  const correctK = viewKey(correctView);

  // Generate 4 unique incorrect options
  const seen = new Set([correctK]);
  const wrongs = [];
  let att = 0;
  while (wrongs.length < 4 && att < 60) {
    const w = makeIncorrect(correctView, faces);
    const k = viewKey(w);
    if (!seen.has(k)) { seen.add(k); wrongs.push(w); }
    att++;
  }
  while (wrongs.length < 4) wrongs.push(makeIncorrect(correctView, faces));

  // Place correct at random position among 5 options
  const options = [...wrongs];
  const correctPos = Math.floor(Math.random() * 5);
  options.splice(correctPos, 0, correctView);

  return {
    taskNumber,
    unfoldedPattern,
    options,
    correctAnswer: correctPos + 1,
  };
};

export const generatePPTTaskSet = (numTasks = 20) =>
  Array.from({ length: numTasks }, (_, i) => generatePPTTask(i + 1));
